import { auth } from '../firebase';

/**
 * Authenticated fetch wrapper that handles token management and 401 responses
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} The fetch response
 */
export async function authenticatedFetch(url, options = {}) {
  try {
    // Get current user's token from Firebase (auto-refreshed by SDK)
    const user = auth.currentUser;
    
    if (!user) {
      // No user logged in, redirect to login
      window.location.href = '/login';
      throw new Error('No authenticated user');
    }

    const token = await user.getIdToken();

    // Merge authorization header with existing headers
    // Don't set Content-Type if body is FormData (browser sets it with boundary)
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
    
    // Only add Content-Type for JSON if not already set and body is not FormData
    if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Make the fetch request
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      console.warn('401 Unauthorized - Session expired, logging out...');
      
      // Sign out from Firebase
      await auth.signOut();
      
      // Redirect to login page
      window.location.href = '/login';
      
      throw new Error('Session expired. Please login again.');
    }

    // Log full error details for non-success responses (especially 5xx)
    if (!response.ok) {
      let errorBody = null;
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          errorBody = await response.clone().json();
        } else {
          errorBody = await response.clone().text();
        }
      } catch (parseErr) {
        errorBody = '(could not parse response body)';
      }
      
      console.error(`API request failed with status ${response.status}:`, {
        url,
        method: options.method || 'GET',
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      });

      // Also log the parsed body separately so it is easy to expand in DevTools
      if (errorBody && typeof errorBody === 'object') {
        console.error('500 response body (expand this object):', errorBody);
        // Force a readable string version so the exact message is visible even when collapsed
        try {
          console.error('500 response body (stringified):', JSON.stringify(errorBody, null, 2));
        } catch (_) {}
      } else if (errorBody) {
        console.error('500 response body (raw):', errorBody);
      }
    }

    return response;
  } catch (error) {
    // If error is due to no user or 401, it's already handled above
    if (error.message.includes('Session expired') || error.message.includes('No authenticated user')) {
      throw error;
    }
    
    // For other errors, log and rethrow
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * Helper function to get current user's token
 * @returns {Promise<string|null>} The ID token or null if not authenticated
 */
export async function getCurrentToken() {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is logged in
 */
export function isAuthenticated() {
  return auth.currentUser !== null;
}
